import { useState, type FormEvent } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  CheckCircle2,
  DownloadCloud,
  Hash,
  Send,
} from "lucide-react";
import { OGI_EXCLUSIONS, type OgiRetrieveResponse } from "@ir/types";
import { retrieveOgiQuote } from "@ir/services";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  PageHeader,
} from "@ir/ui";

/**
 * Pulls an existing OpenGI quote into the marketplace.
 *
 * The exclusions list is a hard prerequisite, not decoration — OGI risks with
 * any of these attributes cannot be mapped cleanly, so it sits above the form
 * where it has to be read before the reference is typed.
 */
export function OgiRetrievePage() {
  const [quoteRef, setQuoteRef] = useState("");
  const [result, setResult] = useState<OgiRetrieveResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = quoteRef.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      setResult(await retrieveOgiQuote(trimmed));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "That retrieval failed.");
    } finally {
      setLoading(false);
    }
  };

  const notImplemented = result?.message === "not-yet-implemented";

  return (
    <>
      <PageHeader
        icon={DownloadCloud}
        title="Retrieve from OpenGI"
        description="Bring an existing OGI quote into the marketplace so it can be re-priced."
      />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] xl:items-start">
        <div className="space-y-4">
          <Card accent="crimson">
            <CardHeader
              icon={AlertTriangle}
              title="Check the quote first"
              description="Retrieval only works for straightforward risks. Do not use it if the quote contains any of these."
            />
            <ul className="grid gap-2 sm:grid-cols-2">
              {OGI_EXCLUSIONS.map((exclusion) => (
                <li
                  key={exclusion}
                  className="flex items-start gap-2 text-sm text-content-muted"
                >
                  <span
                    aria-hidden="true"
                    className="mt-1.5 size-1.5 shrink-0 rounded-full bg-crimson-600"
                  />
                  {exclusion}
                </li>
              ))}
            </ul>
          </Card>

          <Card>
            <form onSubmit={(event) => void onSubmit(event)} className="space-y-4">
              <Field
                label="OpenGI quote reference"
                htmlFor="ogi-ref"
                required
                hint="The reference exactly as it appears in OpenGI."
              >
                <Input
                  id="ogi-ref"
                  required
                  icon={Hash}
                  placeholder="e.g. ISNX135PC1"
                  value={quoteRef}
                  autoComplete="off"
                  onChange={(event) => setQuoteRef(event.target.value)}
                />
              </Field>
              <Button type="submit" icon={Send} loading={loading} fullWidth>
                Retrieve quote
              </Button>
            </form>
          </Card>
        </div>

        <div className="space-y-4">
          {error && (
            <Alert tone="danger" title="Retrieval failed">
              {error}
            </Alert>
          )}

          {notImplemented && (
            <Alert tone="warning" title="OGI retrieval is not live yet">
              The service accepted the reference, but the granite OGI forwarder
              is still being built. Nothing has been imported.
            </Alert>
          )}

          {result && !notImplemented && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              {result.quoteNumber && (
                <Alert tone="success" title="Quote imported">
                  Reference{" "}
                  <span className="font-mono font-semibold">{result.quoteNumber}</span> is
                  now in the marketplace.
                </Alert>
              )}

              {Array.isArray(result.FurtherInfo) && result.FurtherInfo.length > 0 && (
                <Alert tone="warning" title="Further information required">
                  <pre className="mt-1 overflow-x-auto whitespace-pre-wrap font-mono text-xs">
                    {JSON.stringify(result.FurtherInfo, null, 2)}
                  </pre>
                </Alert>
              )}
            </motion.div>
          )}

          {result && (
            <Card>
              <CardHeader
                icon={CheckCircle2}
                title="Full response"
                description="Raw payload returned by the service."
                actions={
                  result.timestamp ? (
                    <Badge tone="neutral">{result.timestamp}</Badge>
                  ) : undefined
                }
              />
              <pre className="max-h-[28rem] overflow-auto rounded-lg bg-surface-sunken p-3 font-mono text-xs leading-relaxed text-content-muted">
                {JSON.stringify(result, null, 2)}
              </pre>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
